import { FC } from 'react';
import MuiChip from '@mui/material/Chip';
import { Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Icon from '../icon';
import { IconColor, IconType } from '../icon/helpers';
import { getOpaqueSurfaceColors } from '@/lib/theme/opaque-surface-colors';
import { ChipColor, ChipSize } from './helpers';

interface Props {
  icon?: IconType;
  iconColor?: IconColor;
  label: string;
  color?: ChipColor; // @todo mui type for this?
  size?: ChipSize;
  className?: string;
}

const useStyles = createUseStyles((theme: Theme) => {
  const chipSurfaces = getOpaqueSurfaceColors(theme);
  return {
    chip: {
      width: 'fit-content',
      // default (colorless) chips render themed grays instead of MUI's defaults
      '&.default': {
        '& .MuiChip-label': {
          color: theme.palette.chip.default,
        },
      },
      '&.info': {
        backgroundColor: theme.palette.chip.infoBackground,
        '& .MuiChip-label': {
          color: theme.palette.chip.info,
        },
      },
      '&.error': {
        backgroundColor: chipSurfaces.error,
        '& .MuiChip-label': {
          color: theme.palette.chip.error,
        },
      },
      '&.warning': {
        backgroundColor: chipSurfaces.warning,
        '& .MuiChip-label': {
          color: theme.palette.chip.warning,
        },
      },
      '&.success': {
        backgroundColor: chipSurfaces.success,
        '& .MuiChip-label': {
          color: theme.palette.chip.success,
        },
      },
      '&.primary': {
        backgroundColor: theme.palette.primary.main,
        '& .MuiChip-label': {
          color: theme.palette.chip.primary,
        },
      },
      '&.secondary': {
        backgroundColor: theme.palette.secondary.light,
        '& .MuiChip-label': {
          color: theme.palette.chip.grey,
        },
      },
      '&.grey': {
        backgroundColor: theme.palette.chip.greyBackground,
        '& .MuiChip-label': {
          color: theme.palette.chip.grey,
        },
      },
      '&.critical': {
        backgroundColor: theme.palette.chip.criticalBackground,
        '& .MuiChip-label': {
          color: theme.palette.chip.critical,
        },
      },
      '&.small': {
        height: 25,
      },
      '&.x-small': {
        height: 22,
        fontSize: theme.typography.caption.fontSize,
      },
      '& .MuiChip-avatar': {
        fontSize: 'inherit !important',
        width: 'auto',
        height: 'auto',
      },
      '&.withIcon': {
        paddingLeft: 7,
      },
    },
  };
});

const Chip: FC<Props> = ({
  label,
  size = 'medium',
  color = 'default',
  className = '',
  icon,
  iconColor,
}) => {
  const styles = useStyles();
  const compClass = classNames(styles.chip, className, color, size, { withIcon: !!icon });
  const muiSize = size === 'x-small' ? 'small' : size;
  const IconEl = icon ? <Icon size={muiSize} name={icon} color={iconColor} /> : undefined;

  return (
    <MuiChip avatar={IconEl} className={compClass} label={label} size={muiSize} variant="filled" />
  );
};

export default Chip;
